import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Shield, Key, Eye, Lock,
  CheckCircle, Users, Globe, Server
} from 'lucide-react';
import { Button } from '../components/ui/button';

const certifications = [
  { name: 'SOC 2 Type II', status: 'Certified', desc: 'Annual audit by independent third party' },
  { name: 'GDPR', status: 'Compliant', desc: 'EU data protection regulation' },
  { name: 'CCPA', status: 'Compliant', desc: 'California consumer privacy' },
  { name: 'ISO 27001', status: 'In Progress', desc: 'Information security management' }
];

const securityPrinciples = [
  {
    title: 'Delegated Identity',
    icon: Users,
    description: 'Every action carries a full principal chain: who requested, who approved, what agent executed, and what credentials were used.',
    details: ['User attribution on every action', 'Agent and connector identification', 'Service account traceability']
  },
  {
    title: 'Least Privilege',
    icon: Key,
    description: 'Scoped approval tokens with explicit boundaries. Time-bound, resource-bound, and use-limited.',
    details: ['No standing access', 'Tokens expire automatically', 'Single-use for sensitive actions']
  },
  {
    title: 'Policy Gating',
    icon: Shield,
    description: 'Actions blocked before execution if policy checks fail. Evidence explains every block.',
    details: ['Pre-execution policy checks', 'Evidence-backed decisions', 'Clear resolution paths']
  },
  {
    title: 'Audit Trail',
    icon: Eye,
    description: 'Every event logged and retained. Exportable, queryable, SIEM-compatible.',
    details: ['7-year retention standard', '10-year retention optional', 'JSON format, API accessible']
  }
];

const dataHandling = [
  { item: 'Data in transit', value: 'TLS 1.3 encryption' },
  { item: 'Data at rest', value: 'AES-256 encryption' },
  { item: 'PII handling', value: 'Processed in transit, not stored' },
  { item: 'Secrets', value: 'Customer-managed vaults (HashiCorp, AWS)' },
  { item: 'Logs', value: 'Secrets redacted, hashes preserved' }
];

const dataResidency = [
  { region: 'United States', locations: 'US-East, US-West', status: 'Available' },
  { region: 'European Union', locations: 'EU-West (Ireland)', status: 'Available' },
  { region: 'Asia Pacific', locations: 'AP-Southeast (Singapore)', status: 'Planned Q2 2026' }
];

export const SecurityGovernance = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="security-governance-page">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-xs">V</span>
              </div>
              <span className="text-sm font-semibold text-slate-900">Vedaya</span>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/workflows" className="text-sm text-muted-foreground hover:text-slate-900">Workflows</Link>
              <Link to="/autonomy" className="text-sm text-muted-foreground hover:text-slate-900">Autonomy</Link>
              <Link to="/integrations" className="text-sm text-muted-foreground hover:text-slate-900">Integrations</Link>
              <Link to="/evidence" className="text-sm text-muted-foreground hover:text-slate-900">Evidence</Link>
              <Link to="/security-governance" className="text-sm font-medium text-primary">Security</Link>
              <Link to="/pricing" className="text-sm text-muted-foreground hover:text-slate-900">Pricing</Link>
            </nav>
            <Button asChild size="sm">
              <Link to="/control-tower">Open Control Tower</Link>
            </Button>
          </div>
        </div>
      </header>

      <section className="py-10 px-6 border-b border-border">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <span className="badge-status badge-active">SOC 2 Type II Certified</span>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">Security and Governance</h1>
          <p className="text-slate-600">
            Delegated identity, least-privilege design, and full audit trails for security-conscious organizations.
          </p>
        </div>
      </section>

      <section className="py-10 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Certifications and Compliance</h2>

          <div className="grid md:grid-cols-4 gap-3">
            {certifications.map((cert, idx) => (
              <div key={idx} className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`badge-status ${
                    cert.status === 'Certified' || cert.status === 'Compliant'
                      ? 'badge-active'
                      : 'badge-pending'
                  }`}>
                    {cert.status}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">{cert.name}</h3>
                <p className="text-sm text-slate-500">{cert.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 px-6 bg-slate-50 border-y border-border">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Security Principles</h2>

          <div className="grid md:grid-cols-2 gap-4">
            {securityPrinciples.map((principle, idx) => (
              <div key={idx} className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    <principle.icon className="w-4 h-4 text-slate-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900">{principle.title}</h3>
                </div>
                <p className="text-sm text-slate-600 mb-3">{principle.description}</p>
                <ul className="space-y-1">
                  {principle.details.map((detail, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-500">
                      <CheckCircle className="w-3 h-3 text-primary" />
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Lock className="w-4 h-4 text-slate-500" />
                Data Handling
              </h2>
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                {dataHandling.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between px-4 py-3 border-b border-slate-100 last:border-0">
                    <span className="text-sm text-slate-600">{item.item}</span>
                    <span className="text-sm font-medium text-slate-900">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Globe className="w-4 h-4 text-slate-500" />
                Data Residency
              </h2>
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="grid grid-cols-3 bg-slate-50 border-b border-slate-200 px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <div>Region</div>
                  <div>Locations</div>
                  <div>Status</div>
                </div>
                {dataResidency.map((region, idx) => (
                  <div key={idx} className="grid grid-cols-3 px-4 py-3 border-b border-slate-100 last:border-0 text-sm">
                    <div className="font-medium text-slate-900">{region.region}</div>
                    <div className="text-slate-600">{region.locations}</div>
                    <div>
                      <span className={`badge-status ${
                        region.status === 'Available' ? 'badge-active' : 'badge-pending'
                      }`}>
                        {region.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 px-6 bg-slate-50 border-y border-border">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Server className="w-4 h-4 text-slate-500" />
            System Coexistence
          </h2>

          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <p className="text-sm text-slate-600 mb-3">
              Vedaya is an overlay, not a replacement. Outcomes sync to your existing systems:
            </p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <CheckCircle className="w-3.5 h-3.5 text-primary" />
                Tickets created and updated in Jira, Zendesk, Freshservice
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <CheckCircle className="w-3.5 h-3.5 text-primary" />
                Audit events sent to your SIEM (Splunk, Datadog, Elastic)
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <CheckCircle className="w-3.5 h-3.5 text-primary" />
                Status synced to HRIS where supported
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <CheckCircle className="w-3.5 h-3.5 text-primary" />
                Evidence bundles attached to tickets for audit continuity
              </li>
            </ul>
            <p className="text-sm text-slate-500 mt-3">
              Your existing tools remain the system of record for compliance.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 px-6 bg-slate-50 border-t border-border">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Review security in the Control Tower</h2>
            <p className="text-sm text-slate-600">View audit trails, policy gates, and principal chains in action.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild variant="outline">
              <Link to="/evidence">Evidence Model</Link>
            </Button>
            <Button asChild>
              <Link to="/control-tower">
                Open Control Tower
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <footer className="py-6 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-2xs">V</span>
            </div>
            <span className="text-xs text-slate-500">Vedaya AI</span>
          </div>
          <p className="text-xs text-slate-400">© 2026 Vedaya AI Inc.</p>
        </div>
      </footer>
    </div>
  );
};

export default SecurityGovernance;
